import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomTooltip from '../../common-components/CustomTooltip/CustomTooltip';
import PopoverComponent from '../../common-components/OnboardingComponents/PopoverComponent';
import SavingProgressDialog from '../../common-components/SavingProgressDialog/SavingProgressDialog';

export default function NavbarItem({
  nav,
  title,
  icon,
  showOnboarding,
  onboardingData,
  disabled = false,
  active,
  badgeCount = 0,
  isBetaFeature = false,
  isExpanded,
  tooltipText,
  betaExternalCondition = null,
  isUserOnPreservationPlan = false,
  isNew = false,
  onClick = () => {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const linkRef = useRef();
  const location = useLocation();
  const { onboardingStep } = useSelector((state) => state.onboarding);
  const { isBetaUser } = useSelector((state) => state.user);
  const { isSaving, dialogConfig } = useSelector((state) => state.saving);
  const [pendingNav, setPendingNav] = useState(null);
  const navigate = useNavigate();

  const checkIfActive = () => {
    if (disabled) {
      return false;
    }

    if (onboardingStep > 0 && [1, 11, 12, 13, 14].includes(onboardingStep)) {
      return false;
    }

    if (active && active.some((a) => location.pathname.includes(a))) {
      return true;
    }
    if (
      location.pathname === `/dashboard${nav.length > 0 ? '/' + nav : nav}` ||
      location.pathname === `/dashboard${nav.length > 0 ? '/' + nav : nav}/`
    )
      return true;
  };

  useEffect(() => {
    if (showOnboarding) {
      setAnchorEl(linkRef.current);
    }
  }, [showOnboarding]);

  const isBetaCondition = betaExternalCondition === null ? isBetaFeature && !isBetaUser : betaExternalCondition;

  const tooltipMessage = disabled
    ? tooltipText || 'Work in progress. Coming soon!'
    : tooltipText || (isBetaCondition ? "It's out, it's private. Reach out in the chat for access!" : title);

  const destination = (function () {
    const betaCondition = betaExternalCondition === null ? isBetaFeature && !isBetaUser : betaExternalCondition;
    if (betaCondition) return '';
    if (disabled) return '';
    if (isUserOnPreservationPlan) return '/dashboard/relationship';
    // Handle empty nav string for dashboard homepage
    return nav === '' ? '/dashboard' : `/dashboard/${nav}`;
  })();

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSaving) {
      if (!pendingNav) setPendingNav(destination);
    } else {
      if (onClick) onClick(e);
      if (destination) navigate(destination);
    }
  };

  useEffect(() => {
    if (pendingNav && !isSaving) {
      navigate(pendingNav);
      setPendingNav(null);
    }
  }, [pendingNav, isSaving, navigate]);

  if (isBetaCondition) return <></>;

  return (
    <>
      <CustomTooltip title={tooltipMessage} placement="right" show={!isExpanded || disabled || isBetaCondition}>
        <div ref={linkRef} className="w-full" onClick={handleClick}>
          <div className="cursor-pointer">
            <Badge
              showZero={badgeCount !== 0}
              color="error"
              badgeContent={badgeCount}
              sx={{
                width: '100%',
                color: '#fff',
                fontSize: '12px',
                '& .MuiBadge-badge': {
                  right: isExpanded ? 16 : 10,
                  top: isExpanded ? 6 : 6,
                  backgroundColor: '#dc2626 !important',
                  display: isExpanded ? 'none' : 'block',
                },
                whiteSpace: 'nowrap',
              }}>
              <div
                className={checkIfActive() ? `active-item navbar-item ${nav}` : `navbar-item navbar-hover ${nav}`}
                style={{
                  justifyContent: !isExpanded ? 'center' : undefined,
                  opacity: disabled && '70%',
                }}>
                <span style={{ fontSize: isExpanded ? '13px' : '16px' }}>{isUserOnPreservationPlan ? '🔒' : icon}</span>
                {isExpanded && (
                  <div>
                    <p
                      className={nav.includes('not-found') ? 'navbar-title-gray' : ''}
                      style={{
                        color: isBetaCondition ? 'var(--text-disabled)' : '',
                      }}>
                      {title}{' '}
                      {badgeCount !== 0 ? (
                        <div className={`navitem-badge-count ${badgeCount > 99 ? 'navitem-badge-99-plus' : ''}`}>
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </div>
                      ) : (
                        ''
                      )}
                      {isNew && <span className="navitem-new-tag">New</span>}
                    </p>
                  </div>
                )}
              </div>
            </Badge>
          </div>
        </div>
      </CustomTooltip>

      <PopoverComponent anchorEl={anchorEl} setAnchorEl={setAnchorEl} onboardingData={onboardingData} />

      <SavingProgressDialog
        open={isSaving && pendingNav !== null}
        title={dialogConfig.title}
        description={dialogConfig.description}
        icon={dialogConfig.icon}
        hideProgress={dialogConfig.hideProgress}
      />
    </>
  );
}
